import { useEffect, useRef, useState } from 'react';
import { CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import FixIt from './FixIt';
import CompetitivePanel from './CompetitivePanel';
import QuestionTrends from './QuestionTrends';
import { Card, Stat, StatRow, SectionLabel } from './ui';
import logoUrl from '/logo.png';
import { getCompetitive, deleteScanRun, getQuestionTrends, type CompetitiveView, type QuestionTrend } from '../account';
import Info from './Glossary';
import QuerySetStats from './QuerySetStats';
import EngineDisagreement from './EngineDisagreement';
import FixOutcomes from './FixOutcomes';
import { externalHref } from '../lib/url';
import ClaimYourPages from './ClaimYourPages';
import RunRivals from './RunRivals';
import type { FaqItem, ScanResult } from '../types';
import { standingOf, facetsOf, setLabel, type Standing } from '../lib/standing';
import { actionPlans, type ActionPlan } from '../lib/actions';
import ActionPlanView from './ActionPlan';

// One record = one full scan (readiness + visibility + footprint) — #3b.
export interface ScanRun {
  id: string; createdAt: string; domain: string; url: string; engine: string;
  readinessScore: number | null; readinessEngine: string | null;
  visTotal: number | null; visMentioned: number | null; visCited: number | null;
  fpKnows: string | null; fpTotal: number | null; fpMentioned: number | null; fpCited: number | null;
  /** Fingerprint of the questions this run asked (D-30). */
  questionSet?: string | null;
  /** The named set it asked, snapshotted at run time (null before migration 015
   * and on the free 5-question path, which uses no set). */
  querySetId?: string | null;
  querySetName?: string | null;
}
export interface ScanRunPage { rows: ScanRun[]; total: number; page: number; pageSize: number; domains: string[]; querySets?: string[] }
export interface ScanRunDetail extends ScanRun { detail: { readiness: unknown; visibility: unknown[]; footprint: unknown } }
interface DetailProbe { query: string; state: string; stable?: boolean; detail?: string; answer?: string; answerTruncated?: boolean; citations?: string[]; competitors?: string[] }
interface FootprintDetail { knowledge?: { knows?: string; summary?: string }; probes?: DetailProbe[] }

export type RunsFetcher = (opts: { page: number; domain?: string; engine?: string; from?: string; to?: string; querySet?: string }) => Promise<ScanRunPage>;
export type RunDetailFetcher = (id: string) => Promise<ScanRunDetail>;
export type RunsComparisonFetcher = (domain?: string) => Promise<ScanRun[]>;

const pct = (n: number | null, total: number | null): number | null => (n != null && total ? Math.round((n / total) * 100) : null);
export const engineLabel = (e: string | null): string => (e === 'gemini' ? 'Gemini' : e === 'openai' ? 'GPT' : e === 'anthropic' ? 'Claude' : e ?? '-');

// Colour = metric, line style = engine (solid Gemini, dashed GPT).
//
// The palette was six hues and failed validation twice over: green↔amber were
// ΔE 5.4 apart for deuteranopes (below the 6 floor — a red-green colourblind
// reader could not separate "AI knows brand" from "mentioned"), and green↔cyan
// were ΔE 11.5 even in normal vision. Two of those hues were also the app's
// status colours doing double duty as series, which the eye reads as good/bad
// where no judgement is meant.
//
// Now four hues, validated (worst all-pairs ΔE 13.4 CVD / 19.4 normal), with
// red and green freed for status only. Two are shown by default — the story is
// "am I named more than last time", not eleven lines.
const PROBE_METRICS = [
  { key: 'visMentioned', name: 'Named %', color: '#7CC4FF', ink: '#1E6FB8', on: true, val: (r: ScanRun) => pct(r.visMentioned, r.visTotal) },
  { key: 'visCited', name: 'Cited %', color: '#B072E0', ink: '#6B3AA0', on: false, val: (r: ScanRun) => pct(r.visCited, r.visTotal) },
  { key: 'fpMentioned', name: 'Footprint named %', color: '#E0B341', ink: '#A8760A', on: false, val: (r: ScanRun) => pct(r.fpMentioned, r.fpTotal) },
  { key: 'fpCited', name: 'Footprint cited %', color: '#B072E0', ink: '#6B3AA0', on: false, val: (r: ScanRun) => pct(r.fpCited, r.fpTotal) },
  { key: 'fpKnows', name: 'AI knows brand', color: '#E0B341', ink: '#A8760A', on: false, val: (r: ScanRun) => (r.fpKnows ? (r.fpKnows !== 'no' ? 100 : 0) : null) },
] as const;
const ENGINES = [{ id: 'gemini', label: 'Gemini', dash: undefined }, { id: 'openai', label: 'GPT', dash: '4 3' }] as const;

/** Metric families the chart can show. Readiness is always available. */
/**
 * Readiness is not on this chart.
 *
 * It is a 0-100 score for how a page is BUILT; the others are shares of
 * questions actually answered. Drawing them on one axis mixes a prediction with
 * a result, and a readiness of 97 flattens a visibility line moving between 8
 * and 12 into a rule along the bottom. Readiness has its own column in the run
 * table and its own KPI, where it is not competing with a different quantity.
 */
export const METRIC_TOGGLES = [
  ...PROBE_METRICS.map((m) => ({ key: m.key, name: m.name, color: m.color, default: m.on })),
];

interface ChartLine { key: string; name: string; color: string; dash: string | undefined }
const linesFor = (ink: boolean, shown: Set<string>): ChartLine[] => [
  ...PROBE_METRICS.filter((m) => shown.has(m.key)).flatMap((m) => ENGINES.map((e) => ({
    key: `${m.key}__${e.id}`, name: `${m.name} · ${e.label}`, color: ink ? m.ink : m.color, dash: e.dash as string | undefined,
  }))),
];

type Series = Record<string, string | number | null>[];

/**
 * The trend chart, in either the dark screen palette or the light print one.
 *
 * `breaks` are the points where the question set changed. Without them the line
 * runs straight through and quietly compares two different measurements — the
 * chart would look fine and be untrue, which is worse than no chart.
 */
function TrendChart({ series, lines, breaks = [], ink = false }: { series: Series; lines: ChartLine[]; breaks?: string[]; ink?: boolean }): JSX.Element {
  const axis = ink ? '#666' : '#A0A0AC';
  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={series} margin={{ top: 8, right: 12, bottom: 20, left: -18 }}>
        {/* Hairline grid, one shade off the surface — recessive, never dashed. */}
        <CartesianGrid stroke={ink ? '#E4E4E4' : '#22222A'} vertical={false} />
        {breaks.map((t) => (
          <ReferenceLine key={t} x={t} stroke={ink ? '#B4291A' : '#E0574A'} strokeDasharray="3 3"
            label={{ value: 'questions changed', position: 'insideTopRight', fill: ink ? '#B4291A' : '#E0574A', fontSize: 10 }} />
        ))}
        <XAxis dataKey="t" tick={{ fill: axis, fontSize: 10 }} tickLine={false} axisLine={{ stroke: ink ? '#E4E4E4' : '#22222A' }} />
        <YAxis domain={[0, 100]} tick={{ fill: axis, fontSize: 10 }} tickLine={false} axisLine={false} width={44} />
        {!ink && (
          <Tooltip
            cursor={{ stroke: '#3A3A45' }}
            contentStyle={{ background: '#1A1A20', border: '1px solid #22222A', borderRadius: 8, fontSize: 12 }}
            labelStyle={{ color: '#A0A0AC' }}
            formatter={(v: number | string, n: string) => [`${v}%`, n]}
          />
        )}
        {lines.length > 1 && <Legend wrapperStyle={{ fontSize: 11, color: ink ? '#333' : undefined }} iconType="plainline" />}
        {lines.map((l) => (
          <Line key={l.key} type="monotone" dataKey={l.key} name={l.name} stroke={l.color} strokeDasharray={l.dash}
            strokeWidth={2} dot={{ r: 2.5, strokeWidth: 0 }} activeDot={{ r: 4.5 }} connectNulls isAnimationActive={false} />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

export const visLabel = (r: ScanRun): string => (r.visTotal != null ? `${r.visMentioned}/${r.visTotal} named` : '-');
export const fpLabel = (r: ScanRun): string => (r.fpTotal ? `${r.fpKnows === 'no' ? 'unknown' : 'knows'} · ${r.fpMentioned}/${r.fpTotal} named` : 'not asked');

/**
 * Run history (#3b): one row per full scan, filterable by domain/engine/date, with
 * a per-engine trend chart, a latest-per-engine comparison, and a click-through
 * detail (readiness + the generated questions/answers + footprint).
 */
/** Chart points, oldest first — the shape Recharts wants. */
function seriesOf(rows: ScanRun[]): Record<string, string | number | null>[] {
  return [...rows].reverse().map((r) => {
    const point: Record<string, string | number | null> = { t: new Date(r.createdAt).toLocaleDateString(), readiness: r.readinessScore };
    for (const m of PROBE_METRICS) point[`${m.key}__${r.engine}`] = m.val(r);
    return point;
  });
}

/** Where the question set changed between consecutive runs — the trend cannot
 * honestly be read across such a point. */
function breaksOf(rows: ScanRun[]): string[] {
  const ordered = [...rows].reverse();
  const out: string[] = [];
  for (let i = 1; i < ordered.length; i++) {
    const prev = ordered[i - 1]!.questionSet;
    const cur = ordered[i]!.questionSet;
    if (prev && cur && prev !== cur) out.push(new Date(ordered[i]!.createdAt).toLocaleDateString());
  }
  return out;
}

export default function ScanHistory({ fetcher, detailFetcher, comparisonFetcher, plan, viewer, readOnly }: {
  fetcher: RunsFetcher; detailFetcher: RunDetailFetcher; comparisonFetcher?: RunsComparisonFetcher;
  /** Viewer's plan — enables the Pro Fix it plan in the detail modal (#7). Omit for admin. */
  plan?: 'free' | 'pro';
  /**
   * Whose report this is, for the PDF header. Passed in rather than read from
   * the account context: this component is also rendered inside the admin, which
   * mounts outside AccountProvider — calling the hook here crashed the admin's
   * customer view outright.
   */
  viewer?: { email: string } | null;
  /**
   * A share-link viewer. Read-only, so the actions that write are not drawn.
   *
   * The server refuses them either way; this is so a guest does not click a
   * button and get a 403 they cannot do anything about.
   */
  readOnly?: boolean;
}): JSX.Element {
  const [domain, setDomain] = useState('');
  const [engine, setEngine] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<ScanRunPage | null>(null);
  const [chart, setChart] = useState<ScanRun[]>([]);
  const [domains, setDomains] = useState<string[]>([]);
  const [querySet, setQuerySet] = useState('');
  const [setNames, setSetNames] = useState<string[]>([]);
  const [comparison, setComparison] = useState<ScanRun[]>([]);
  const [detail, setDetail] = useState<ScanRunDetail | null>(null);
  const [shown, setShown] = useState<Set<string>>(() => new Set(METRIC_TOGGLES.filter((m) => m.default).map((m) => m.key)));
  // The printed report carries the competitor section too, so it is fetched once
  // here rather than only inside the on-screen panel.
  const [competitive, setCompetitive] = useState<CompetitiveView | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Runs ticked for the report. Empty = the report covers the whole filtered range. */
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const [deleting, setDeleting] = useState<string | null>(null);
  /**
   * The per-question detail for the run the PDF is really about.
   *
   * The list endpoint returns summary rows only, so the export could show counts
   * and a trend but never the questions themselves — which is the part anyone
   * receiving the report actually wants to read. Fetched for the single picked
   * run, or the newest in range when nothing is picked.
   */
  const [reportDetail, setReportDetail] = useState<ScanRunDetail | null>(null);
  /**
   * Every question in the filtered range, per model, for the PDF.
   *
   * The report could show one run's questions grouped by outcome, which is one
   * engine on one day, and never said which model gave which answer. Somebody
   * reading the PDF could not tell whether "not named" meant both models or one.
   * This is the same data the on-screen table uses, so the two cannot drift.
   */
  const [reportQuestions, setReportQuestions] = useState<QuestionTrend[]>([]);

  const q = { domain: domain || undefined, engine: engine || undefined, from: from || undefined, to: to || undefined, querySet: querySet || undefined };

  useEffect(() => {
    fetcher({ page, ...q }).then((d) => { setData(d); if (d.domains.length) setDomains(d.domains); if (d.querySets?.length) setSetNames(d.querySets); })
      .catch((e) => setError(e instanceof Error ? e.message : 'failed'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, domain, engine, from, to, querySet, fetcher]);

  useEffect(() => {
    getQuestionTrends(q).then((d) => setReportQuestions(d.questions)).catch(() => setReportQuestions([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, engine, from, to, querySet]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const acc: ScanRun[] = [];
      for (let p = 1; p <= 10; p++) {
        const d = await fetcher({ page: p, ...q });
        acc.push(...d.rows);
        if (acc.length >= d.total || d.rows.length === 0) break;
      }
      if (!cancelled) setChart(acc);
    })().catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, engine, from, to, querySet, fetcher]);

  useEffect(() => {
    getCompetitive(q).then(setCompetitive).catch(() => setCompetitive(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, engine, from, to, querySet]);

  useEffect(() => {
    if (!comparisonFetcher) return;
    comparisonFetcher(domain || undefined).then(setComparison).catch(() => setComparison([]));
  }, [domain, comparisonFetcher]);

  const series = seriesOf(chart);
  const breaks = breaksOf(chart);

  // What the PDF covers. Empty selection = everything the filters select, which
  // is the old behaviour; ticking rows narrows the report without touching the
  // filters, so you can publish three runs out of forty without first having to
  // find a date range that isolates them.
  const reportRows = picked.size ? chart.filter((r) => picked.has(r.id)) : chart;

  // Where it stands, from comparable runs only: one headline when the range is
  // one set on one engine, a row per set otherwise (see lib/standing.ts).
  const standing = standingOf(chart);
  const facets = facetsOf(chart);

  /**
   * The detail of the latest run of every set × engine, for "What to change".
   *
   * The list endpoint returns counts only; the to-do list needs the questions,
   * their sources and who was named. Fetched for the screen range and the report
   * range together, cached by id so a filter change does not refetch what we have.
   */
  const latestIds = (rs: ScanRun[]): string[] =>
    standingOf(rs).groups.flatMap((g) => Object.values(g.cells).map((c) => c.latest.id)).slice(0, 24);
  const screenIds = latestIds(chart);
  const reportIds = latestIds(reportRows);
  const detailCache = useRef(new Map<string, ScanRunDetail>());
  const [planTick, setPlanTick] = useState(0);
  const wantKey = [...new Set([...screenIds, ...reportIds])].join(',');
  useEffect(() => {
    let cancelled = false;
    const missing = wantKey.split(',').filter((id) => id && !detailCache.current.has(id));
    if (missing.length === 0) return;
    void Promise.all(missing.map((id) => detailFetcher(id).then((d) => { detailCache.current.set(id, d); }).catch(() => {})))
      .then(() => { if (!cancelled) setPlanTick((n) => n + 1); });
    return () => { cancelled = true; };
  }, [wantKey, detailFetcher]);
  const plansFor = (ids: string[]): ActionPlan[] => {
    void planTick;
    const ds = ids.map((id) => detailCache.current.get(id)).filter((d): d is ScanRunDetail => !!d?.detail && !!d.domain);
    return ds.length ? actionPlans(ds, competitive?.lossSources ?? []) : [];
  };
  const screenPlans = plansFor(screenIds);
  const reportPlans = plansFor(reportIds);

  // Which metric families the chart draws. Two by default — the question is
  // "am I named more than last time", and eleven lines answer nothing.
  const hasData = (key: string): boolean =>
    ENGINES.some((e) => series.some((pt) => pt[`${key}__${e.id}`] != null));
  const available = METRIC_TOGGLES.filter((m) => hasData(m.key));
  const screenLines = linesFor(false, shown).filter((l) => series.some((pt) => pt[l.key] != null));
  const printLines = linesFor(true, shown).filter((l) => series.some((pt) => pt[l.key] != null));
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  /**
   * Delete runs: the one you clicked, or the ones you ticked. A run is a
   * measurement in a trend, so this asks first and names how many — and it only
   * ever removes runs you picked; there is deliberately no "delete all".
   */
  async function removeRuns(ids: string[], what: string): Promise<boolean> {
    if (ids.length === 0) return false;
    if (!window.confirm(`Delete ${what}? ${ids.length === 1 ? 'It disappears' : 'They disappear'} from the trend and the report. This cannot be undone.`)) return false;
    setDeleting(ids.length === 1 ? ids[0]! : 'many');
    const gone: string[] = [];
    try {
      // One at a time: a handful of rows, and a failure part-way leaves the
      // screen matching what was actually deleted.
      for (const id of ids) { await deleteScanRun(id); gone.push(id); }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'could not delete that run');
    } finally {
      const drop = new Set(gone);
      setPicked((prev) => new Set([...prev].filter((id) => !drop.has(id))));
      setChart((prev) => prev.filter((r) => !drop.has(r.id)));
      setData((prev) => (prev ? { ...prev, rows: prev.rows.filter((r) => !drop.has(r.id)), total: Math.max(0, prev.total - drop.size) } : prev));
      if (detail && drop.has(String(detail.id))) setDetail(null);
      setDeleting(null);
    }
    return gone.length === ids.length;
  }
  const removeRun = (id: string, when: string): Promise<boolean> => removeRuns([id], `the run of ${when}`);

  const reportRunId = reportRows[0]?.id ?? null;
  useEffect(() => {
    if (!reportRunId) { setReportDetail(null); return; }
    let cancelled = false;
    detailFetcher(reportRunId)
      .then((d) => { if (!cancelled) setReportDetail(d); })
      .catch(() => { if (!cancelled) setReportDetail(null); });
    return () => { cancelled = true; };
  }, [reportRunId, detailFetcher]);

  function openDetail(id: string): void {
    setDetail(null);
    detailFetcher(id).then(setDetail).catch(() => {});
  }

  return (
    <div className="w-full">
      {/* Filters — one toolbar row above the charts, not loose controls. */}
      <SectionLabel>Filter</SectionLabel>
      <p className="text-sm text-text-secondary mt-1 mb-2">
        Every field applies together, and everything below this line answers to all of them: the set
        table, the rivals, the per-question history, the fix outcomes and the run log.
      </p>

      {/* 56px: the height of the site header, which is fixed. At top-0 the filter
          slid underneath it and the first row of controls disappeared. */}
      <div className="sticky top-14 z-30 flex flex-wrap items-end gap-3 mb-5 p-3 rounded-xl border border-warm-dark-2 bg-warm-dark/95 backdrop-blur-sm shadow-lg shadow-black/30 print:hidden">
        <label className="flex flex-col gap-1 text-sm text-text-secondary">
          <span className="font-mono text-sm">Domain</span>
          <select value={domain} onChange={(e) => { setPage(1); setDomain(e.target.value); }}
            className="bg-warm-dark-2 border border-warm-dark-2 focus:border-cyan/60 rounded px-2 py-1 text-text-primary outline-none font-mono text-sm">
            <option value="">All domains</option>
            {domains.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm text-text-secondary">
          <span className="font-mono text-sm">Engine</span>
          <select value={engine} onChange={(e) => { setPage(1); setEngine(e.target.value); }}
            className="bg-warm-dark-2 border border-warm-dark-2 focus:border-cyan/60 rounded px-2 py-1 text-text-primary outline-none font-mono text-sm">
            <option value="">All engines</option>
            <option value="gemini">Gemini</option>
            <option value="openai">GPT</option>
          </select>
        </label>
        {/* The set is what makes two runs comparable, so it belongs in the same
            row as domain and engine rather than buried in the table. */}
        {setNames.length > 0 && (
          <label className="flex flex-col gap-1 text-sm text-text-secondary">
            <span className="font-mono text-sm">Question set</span>
            <select value={querySet} onChange={(e) => { setPage(1); setQuerySet(e.target.value); }}
              className="max-w-[13rem] bg-warm-dark-2 border border-warm-dark-2 focus:border-cyan/60 rounded px-2 py-1 text-text-primary outline-none font-mono text-sm">
              <option value="">All sets</option>
              {setNames.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
        )}
        <label className="flex flex-col gap-1 text-sm text-text-secondary">
          <span className="font-mono text-sm">From</span>
          <input type="date" value={from} onChange={(e) => { setPage(1); setFrom(e.target.value); }}
            className="bg-warm-dark-2 border border-warm-dark-2 focus:border-cyan/60 rounded px-2 py-1 text-text-primary outline-none font-mono text-sm" />
        </label>
        <label className="flex flex-col gap-1 text-sm text-text-secondary">
          <span className="font-mono text-sm">To</span>
          <input type="date" value={to} onChange={(e) => { setPage(1); setTo(e.target.value); }}
            className="bg-warm-dark-2 border border-warm-dark-2 focus:border-cyan/60 rounded px-2 py-1 text-text-primary outline-none font-mono text-sm" />
        </label>
        {(domain || engine || from || to || querySet) && (
          <button onClick={() => { setDomain(''); setEngine(''); setFrom(''); setTo(''); setQuerySet(''); setPage(1); }} className="font-mono text-sm text-text-secondary hover:text-cyan transition pb-1">clear</button>
        )}

        {/* What the PDF will contain, stated before you press the button — the
            export used to silently follow the filters and there was no way to
            see, let alone change, which runs that meant. */}
        <label className="flex flex-col gap-1 text-sm text-text-secondary ml-auto">
          <span className="font-mono text-sm">Report covers</span>
          <span className="font-mono text-sm text-text-primary bg-warm-dark-2 border border-[#70707D] rounded px-2 py-1">
            {picked.size === 0
              ? `all ${chart.length} ${chart.length === 1 ? 'run' : 'runs'} in range`
              : `${picked.size} selected`}
            {picked.size > 0 && (
              <button onClick={() => setPicked(new Set())} className="ml-2 text-text-secondary hover:text-cyan transition">clear</button>
            )}
          </span>
        </label>
        {picked.size > 0 && !readOnly && (
          <button
            onClick={() => void removeRuns([...picked], `${picked.size} selected ${picked.size === 1 ? 'run' : 'runs'}`)}
            disabled={deleting !== null}
            className="font-mono text-sm text-[#E0574A] border border-[#E0574A]/40 rounded-lg px-3 py-1.5 hover:bg-[#E0574A]/10 disabled:opacity-40 transition"
          >
            {deleting === 'many' ? 'deleting…' : `Delete ${picked.size} selected`}
          </button>
        )}
        <button
          onClick={() => window.print()}
          disabled={reportRows.length === 0}
          title="Builds a PDF of exactly the runs named in “Report covers”, trend chart, comparison, the questions and each run. Opens your browser's print dialog; choose “Save as PDF”."
          className="font-mono text-sm text-cyan border border-cyan/40 rounded-lg px-3 py-1.5 hover:bg-cyan/10 disabled:opacity-40 disabled:hover:bg-transparent transition"
        >
          ↓ Export PDF
        </button>
        {/* The report is dark by design. Chrome and Safari honour
            `print-color-adjust: exact` even with the checkbox off, but Firefox
            does not, and a dark report printed without its backgrounds is white
            text on white paper, so say it rather than let it happen. */}
        <p className="w-full font-mono text-xs text-text-secondary/70 -mt-1">
          The PDF is dark like the app. If your print preview looks blank, tick{' '}
          <span className="text-text-secondary">“Background graphics”</span> in the print dialog.
        </p>
      </div>

      {error && <p className="font-mono text-sm text-[#E0574A] mb-3">{error}</p>}

      {/* Who AI names instead of you, over time (#1/#2). */}
      {/*
        Back under the filter, and answering to all of it.

        It sat above for a while on the theory that a table OF sets cannot be
        narrowed to one set. That was half right and entirely confusing: it was
        above the filter and still changed when the filter did, which is the
        worst of both. Filtering to one set simply shows that set, which is what
        somebody selecting it wants.
      */}
      <QuerySetStats filter={{
        domain: domain || undefined, engine: engine || undefined,
        from: from || undefined, to: to || undefined, querySet: querySet || undefined,
      }} />

      <QuestionTrends filter={q} />

      <CompetitivePanel data={competitive} />

      {/* Model comparison: latest run per engine */}
      {comparison.length > 0 && (
        <div className="mb-5 border border-warm-dark-2 bg-warm-dark-2/20 rounded-xl overflow-x-auto">
          <div className="px-4 pt-3 pb-1">
            <h3 className="text-text-primary">Gemini vs GPT</h3>
            <p className="text-sm text-text-secondary">Latest run per engine{domain ? ` · ${domain}` : ''}. They disagree more than people expect. That&apos;s the models, not your site.
              {new Set(comparison.map(setLabel)).size > 1 && <span className="text-[#E0B341]"> These runs asked different question sets, so their visibility is not comparable. Compare engines per set in Latest below.</span>}</p>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="text-text-secondary font-mono text-sm text-left">
              <th className="py-2 px-3">Engine</th><th className="px-3">Question set</th><th className="px-3">Readiness</th><th className="px-3">Visibility</th><th className="px-3">Footprint</th>
            </tr></thead>
            <tbody>
              {comparison.map((r) => (
                <tr key={r.engine} className="border-t border-warm-dark-2">
                  <td className="py-2 px-3 font-mono text-cyan">{engineLabel(r.engine)}</td>
                  <td className="px-3">{setLabel(r)}</td>
                  <td className="px-3 font-mono">{r.readinessScore ?? '-'}{r.readinessScore != null ? '/100' : ''}</td>
                  <td className="px-3 font-mono">{visLabel(r)}</td>
                  <td className="px-3 font-mono">{fpLabel(r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Where it stands right now — the number is the chart. */}
      {chart.length > 0 && (
        <>
          <SectionLabel>Latest{domain ? ` · ${domain}` : ''}</SectionLabel>
          <StatRow>
            <Stat info="readiness" label="Readiness" value={standing.readiness?.value ?? '-'} unit={standing.readiness ? '/100' : undefined}
              delta={standing.readiness?.delta ?? null}
              tone={!standing.readiness ? 'neutral' : standing.readiness.value >= 70 ? 'good' : standing.readiness.value >= 40 ? 'warn' : 'bad'}
              note="How well the page is built for AI to read it" />
            {standing.single && (
              <>
                <Stat info="mentioned" label="AI named you" value={`${standing.single.latest.visMentioned}/${standing.single.latest.visTotal}`}
                  delta={standing.single.delta} tone={standing.single.latest.visMentioned ? 'good' : 'bad'}
                  note={`Questions where AI says your name · ${setLabel(standing.single.latest)} on ${engineLabel(standing.single.engine)}`} />
                <Stat info="cited_only" label="Used you, didn’t name you" value={`${standing.single.latest.visCited}/${standing.single.latest.visTotal}`}
                  note="AI read your page and answered without you, fix this on the page" />
              </>
            )}
            <Stat info="footprint" label="Knows you unprompted" value={knowsText(standing)}
              tone={Object.values(standing.knows).every((k) => k === 'no') && Object.keys(standing.knows).length ? 'bad' : Object.keys(standing.knows).length ? 'good' : 'neutral'}
              note="From training memory, with no page in front of it" />
          </StatRow>
          {!standing.single && standing.groups.length > 0 && (
            <Card className="p-4 mt-3 overflow-x-auto">
              <p className="text-sm text-text-secondary mb-2">
                This range has {standing.groups.length} question {standing.groups.length === 1 ? 'set' : 'sets'} on {standing.engines.length} {standing.engines.length === 1 ? 'engine' : 'engines'}.
                Each one is its own measurement, so there is no single number for all of them. Latest run of each set, per engine:
              </p>
              <StandingTable standing={standing} />
            </Card>
          )}
          <div className="h-4" />
        </>
      )}

      {screenPlans.length > 0 && (
        <Card className="p-4 mb-5">
          <h3 className="text-text-primary">What to change, and why{domain ? ` · ${domain}` : ''}</h3>
          <p className="text-sm text-text-secondary mt-0.5 mb-4">
            From the latest run of every question set on every engine in this range, in the order it pays back:
            pages AI already reads first, off-site work last. The same list is in the PDF.
          </p>
          <ActionPlanView plans={screenPlans} />
        </Card>
      )}

      {facets.length > 1 ? (
        <Card className="p-4 mb-5">
          <h3 className="text-text-primary">Over time, per question set{domain ? ` · ${domain}` : ''}</h3>
          <p className="text-sm text-text-secondary mt-0.5 mb-3">
            One chart per set: different sets ask different questions, so one line across them would join measurements that
            cannot be compared. Solid is Gemini, dashed is GPT.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            {facets.map((f) => {
              const fs = seriesOf(f.rows);
              const fl = linesFor(false, shown).filter((l) => fs.some((pt) => pt[l.key] != null));
              return (
                <div key={f.label}>
                  <p className="font-mono text-xs text-text-primary mb-1 truncate" title={f.label}>{f.label}</p>
                  {fs.length > 1
                    ? <div className="h-40"><TrendChart series={fs} lines={fl} breaks={breaksOf(f.rows)} /></div>
                    : <p className="font-mono text-xs text-text-secondary">One run ({engineLabel(f.rows[0]!.engine)}: {visLabel(f.rows[0]!)}). A trend needs two.</p>}
                </div>
              );
            })}
          </div>
        </Card>
      ) : series.length > 1 ? (
        <Card className="p-4 mb-5">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
            <div>
              <h3 className="text-text-primary">Over time{domain ? ` · ${domain}` : ' · all domains'}</h3>
              {breaks.length > 0 && (
                <p className="text-sm text-[#E0574A] mt-0.5">
                  Question set changed {breaks.length}× in this range. The line can&apos;t be read straight across those marks.
                </p>
              )}
            </div>
            {/* Which families to draw. Colour follows the metric, so toggling one
                off never repaints the others. */}
            <div className="flex flex-wrap gap-1.5">
              {available.map((m) => {
                const on = shown.has(m.key);
                return (
                  <button
                    key={m.key}
                    onClick={() => setShown((prev) => {
                      const next = new Set(prev);
                      if (next.has(m.key)) next.delete(m.key); else next.add(m.key);
                      return next;
                    })}
                    className={`font-mono text-xs border rounded-full px-2.5 py-1 transition ${on ? 'text-text-primary border-cyan/60 bg-warm-dark-2' : 'text-text-secondary border-[#70707D] hover:text-text-primary hover:border-cyan/60'}`}
                  >
                    <span className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle" style={{ background: on ? m.color : '#3A3A45' }} />
                    {m.name}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="h-64"><TrendChart series={series} lines={screenLines} breaks={breaks} /></div>
        </Card>
      ) : (
        <Card tone="quiet" className="p-4 mb-5">
          <p className="text-sm text-text-secondary">
            One scan so far{domain ? ` for ${domain}` : ''}. A trend needs at least two. Re-scan after you&apos;ve
            changed something and the line starts here.
          </p>
        </Card>
      )}

      {/* The loop: what you said you fixed, and whether it moved. Needs one
          domain, "did it work" is meaningless averaged across sites. */}
      {domain && <FixOutcomes domain={domain} querySet={querySet || undefined} />}

      {/* We keep saying the engines contradict each other; this shows it. */}
      <EngineDisagreement comparison={comparison} detailFetcher={detailFetcher} />

      {/* Sets as their own subject — "which of my sets works" is a different
          question from "what happened on Tuesday". */}
      {/* Table — one row per full scan; click to open detail */}
      <SectionLabel>Every run{domain ? ` · ${domain}` : ''}{querySet ? ` · ${querySet}` : ''}</SectionLabel>
      <p className="text-sm text-text-secondary mt-1">
        Click any row to open that run: every question, what each engine answered, and who was named instead.
      </p>
      {/*
        `overflow-x-auto` establishes a clipping box on BOTH axes, so the
        glossary popovers inside the header cells were cut off at the table edge.
        `overflow-y-visible` is ignored next to an `auto` sibling, so the scroller
        stays and the tooltips are lifted out of it by rendering into a portal
        instead (see Glossary). The wide table still scrolls sideways.
      */}
      <div className="overflow-x-auto border border-warm-dark-2 bg-warm-dark-2/20 rounded-xl mt-3">
        <table className="w-full text-sm">
          <thead><tr className="text-text-secondary font-mono text-sm text-left">
            <th className="py-2 px-3 w-8" title="tick runs to put them in the PDF report, or to delete them">Select</th>
            <th className="py-2 px-3">Date</th>
            <th className="px-3">Domain</th>
            <th className="px-3"><span className="inline-flex items-center gap-1.5">Engine <Info term="engine" /></span></th>
            <th className="px-3"><span className="inline-flex items-center gap-1.5">Questions <Info term="question_set" /></span></th>
            <th className="px-3"><span className="inline-flex items-center gap-1.5">Readiness <Info term="readiness" /></span></th>
            <th className="px-3"><span className="inline-flex items-center gap-1.5">Visibility <Info term="mentioned" /></span></th>
            <th className="px-3"><span className="inline-flex items-center gap-1.5">Footprint <Info term="footprint" /></span></th>
            <th className="px-3"></th><th className="px-3 w-8"></th>
          </tr></thead>
          <tbody>
            {(data?.rows ?? []).map((r) => (
              <tr
                key={r.id}
                onClick={() => openDetail(r.id)}
                title="Open this run"
                className="group border-t border-warm-dark-2 cursor-pointer hover:bg-warm-dark-2/50 hover:ring-1 hover:ring-inset hover:ring-cyan/30 transition">
                <td className="py-2 px-3" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={picked.has(r.id)}
                    onChange={() => setPicked((prev) => {
                      const next = new Set(prev);
                      if (next.has(r.id)) next.delete(r.id); else next.add(r.id);
                      return next;
                    })}
                    className="accent-cyan"
                    aria-label={`select the run of ${new Date(r.createdAt).toLocaleString()}`}
                  />
                </td>
                <td className="py-2 px-3 font-mono text-text-secondary">{new Date(r.createdAt).toLocaleString()}</td>
                <td className="px-3">{r.domain}</td>
                <td className="px-3 font-mono text-cyan/80">{engineLabel(r.engine)}</td>
                <td className="px-3 text-text-secondary max-w-[12rem] truncate" title={r.querySetName ?? undefined}>
                  {r.querySetName ?? <span className="text-text-secondary/40">-</span>}
                </td>
                <td className="px-3 font-mono">{r.readinessScore ?? '-'}{r.readinessScore != null ? '/100' : ''}</td>
                <td className="px-3 font-mono">{visLabel(r)}</td>
                <td className="px-3 font-mono">{fpLabel(r)}</td>
                <td className="px-3 font-mono text-text-secondary/50 group-hover:text-cyan transition">open →</td>
                <td className="px-3" onClick={(e) => e.stopPropagation()}>
                  {/* Not drawn for a share-link viewer. The server refuses it
                      either way; this is so a guest does not click and get a
                      403 they can do nothing about. */}
                  {!readOnly && (
                    <button
                      onClick={() => void removeRun(r.id, new Date(r.createdAt).toLocaleString())}
                      disabled={deleting !== null}
                      title="Delete this run permanently"
                      aria-label={`delete the run of ${new Date(r.createdAt).toLocaleString()}`}
                      className="font-mono text-xs text-text-secondary border border-warm-dark-2 rounded px-2 py-1 hover:text-[#E0574A] hover:border-[#E0574A]/50 disabled:opacity-40 transition"
                    >
                      {deleting === r.id ? '…' : 'Delete'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {data && data.rows.length === 0 && (
              <tr><td colSpan={10} className="py-4 px-3 font-mono text-sm text-text-secondary">No scans in this range.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data && data.total > data.pageSize && (
        <div className="flex items-center justify-between mt-3 font-mono text-sm">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="text-text-secondary hover:text-cyan disabled:opacity-40 transition">← prev</button>
          <span className="text-text-secondary">page {data.page} / {totalPages} · {data.total} scans</span>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="text-text-secondary hover:text-cyan disabled:opacity-40 transition">next →</button>
        </div>
      )}

      {/* PDF report (B4) — always mounted so Recharts has real dimensions, parked
          off-screen until print pulls it to the page origin (see tailwind.css). */}
      <PrintReport rows={reportRows} comparison={comparison} series={seriesOf(reportRows)} lines={printLines} breaks={breaksOf(reportRows)}
        competitive={competitive} filter={{ domain, engine, from, to, selected: picked.size || undefined }}
        detail={reportDetail}
        plans={reportPlans}
        questions={reportQuestions}
        account={viewer ? { email: viewer.email, tier: plan ?? '' } : null} />

      {/* Detail modal */}
      {detail && (
        <RunDetailModal
          run={detail}
          onClose={() => setDetail(null)}
          plan={plan}
          onDelete={readOnly ? undefined : () => void removeRun(String(detail.id), new Date(detail.createdAt).toLocaleString())}
        />
      )}
    </div>
  );
}

/** Unprompted knowledge per engine; one word when only one engine was asked. */
function knowsText(s: Standing): string {
  const word = (k: string): string => (k === 'no' ? 'No' : k === 'well' ? 'Well' : 'Vaguely');
  const entries = s.engines.filter((e) => s.knows[e]).map((e) => [e, s.knows[e]!] as const);
  if (!entries.length) return '-';
  if (entries.length === 1) return word(entries[0]![1]);
  return entries.map(([e, k]) => `${engineLabel(e)} ${word(k)}`).join(' · ');
}

/**
 * One row per question set, one column per engine: the latest named/cited of
 * that pair, and a delta only against the previous run of the same pair.
 */
function StandingTable({ standing, ink = false }: { standing: Standing; ink?: boolean }): JSX.Element {
  const th = ink ? 'text-left py-1.5 px-2 font-semibold' : 'text-left py-1.5 px-2 font-mono text-xs text-text-secondary font-normal';
  const td = ink ? 'py-1.5 px-2 align-top' : 'py-1.5 px-2 align-top';
  const good = ink ? '#6BE07A' : undefined;
  return (
    <table className={ink ? 'w-full' : 'w-full text-sm'} style={{ borderCollapse: 'collapse' }}>
      <thead><tr>
        <th className={th}>Question set</th>
        {standing.engines.map((e) => <th key={e} className={th}>{engineLabel(e)}<br /><span className="font-normal">named · cited</span></th>)}
      </tr></thead>
      <tbody>
        {standing.groups.map((g) => (
          <tr key={g.label} className={ink ? '' : 'border-t border-warm-dark-2'} style={ink ? { borderTop: '1px solid #33333D' } : undefined}>
            <td className={td}>{g.label}</td>
            {standing.engines.map((e) => {
              const c = g.cells[e];
              if (!c) return <td key={e} className={`${td} ${ink ? '' : 'text-text-secondary'}`}>not run</td>;
              const r = c.latest;
              return (
                <td key={e} className={`${td} font-mono`}>
                  <span className={ink ? '' : r.visMentioned ? 'text-success' : ''} style={ink && r.visMentioned ? { color: good } : undefined}>
                    {r.visMentioned}/{r.visTotal}
                  </span>
                  {' · '}{r.visCited}
                  {c.delta != null && c.delta !== 0 && <span className={ink ? '' : 'text-text-secondary'}> ({c.delta > 0 ? '+' : ''}{c.delta})</span>}
                  <div className={ink ? 'text-[8px]' : 'text-xs text-text-secondary'} style={ink ? { color: '#A9A9B4' } : undefined}>
                    {new Date(r.createdAt).toLocaleDateString()}{c.runs > 1 ? ` · ${c.runs} runs` : ''}
                  </div>
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * The printable report.
 *
 * A client-facing document, not a screenshot of the app: portrait A4, black on
 * white, the brand mark and the accent rule but almost no ink beyond that — a
 * report that eats a colour cartridge does not get printed twice. It carries
 * everything the page shows, because the person reading it is usually not the
 * person who ran the scan and cannot go and click around: the headline numbers,
 * the trend, Gemini vs GPT, who is named instead of you, the pages that beat
 * you, and every run in the filtered range.
 *
 * Scope is the active filter, so what you see filtered is what you hand over.
 */
function PrintReport({ rows, comparison, series, lines, breaks, competitive, filter, detail, plans, questions, account }: {
  rows: ScanRun[]; comparison: ScanRun[]; series: Series; lines: ChartLine[]; breaks: string[];
  competitive: CompetitiveView | null;
  filter: { domain: string; engine: string; from: string; to: string; selected?: number };
  /** Per-question detail of the run this report is about, when there is one. */
  detail: ScanRunDetail | null;
  /** What to change, per domain, from the latest run of each set × engine. */
  plans: ActionPlan[];
  /** Every question in range, with its per-model history. */
  questions: QuestionTrend[];
  account?: { email: string; tier: string } | null;
}): JSX.Element {
  const scope = [
    filter.domain || 'All domains',
    filter.engine ? engineLabel(filter.engine) : 'All engines',
    filter.from || filter.to ? `${filter.from || 'start'} → ${filter.to || 'today'}` : 'All dates',
    // Say so on the page itself: a report of hand-picked runs is not the same
    // document as a report of a date range, and the reader cannot tell by eye.
    ...(filter.selected ? [`${filter.selected} hand-picked runs`] : []),
  ].join('  ·  ');

  // Comparable runs only — see lib/standing.ts. The headline used to be rows[0]
  // and its delta against rows[1], whatever sets and engines those were.
  const standing = standingOf(rows);
  const facets = facetsOf(rows);
  const one = standing.single;
  const signed = (d: number | null): string => (d != null && d !== 0 ? `${d > 0 ? '+' : ''}${d}` : '');

  // The report wears the brand rather than office-document white: warm dark
  // ground, cyan accent, one green and one red reserved for status.
  //
  // A dark PDF depends on the browser actually printing backgrounds. Chrome and
  // Safari honour `print-color-adjust: exact` even with "Background graphics"
  // unticked — which is why every painted surface here declares it — but the
  // button on screen says so too, because a report that prints white with white
  // text is worse than an ugly one.
  const PAPER = '#1A1A20', PANEL = '#26262F';
  const INK = '#F2F2F4', MUTED = '#A9A9B4', RULE = '#33333D', ACCENT = '#00FFD4', GOOD = '#6BE07A', BAD = '#E0574A';
  const exact = { WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' } as React.CSSProperties;
  const th = 'text-left py-1.5 px-2 font-semibold';
  const td = 'py-1.5 px-2 align-top';

  const H = ({ children, n }: { children: React.ReactNode; n: string }): JSX.Element => (
    <div className="flex items-baseline gap-2 mt-6 mb-2 pb-1" style={{ borderBottom: `1px solid ${RULE}` }}>
      <span className="font-mono text-[10px]" style={{ color: ACCENT }}>{n}</span>
      <h2 className="text-[13px] font-semibold" style={{ color: INK }}>{children}</h2>
    </div>
  );

  const Tile = ({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'good' | 'bad' }): JSX.Element => (
    <div className="px-3 py-2 rounded-lg" style={{ border: `1px solid ${RULE}`, background: PANEL, ...exact }}>
      <div className="text-[8px] uppercase tracking-[0.14em]" style={{ color: MUTED }}>{label}</div>
      <div className="text-[19px] leading-tight mt-0.5" style={{ color: tone === 'good' ? GOOD : tone === 'bad' ? BAD : INK }}>{value}</div>
      {sub && <div className="text-[8.5px] leading-snug mt-0.5" style={{ color: MUTED }}>{sub}</div>}
    </div>
  );

  return (
    <div
      id="aivs-print-report"
      aria-hidden="true"
      className="fixed top-0 -left-[20000px] w-[760px] font-sans text-[10px] p-6"
      style={{ background: PAPER, color: INK, ...exact }}
    >
      {/* Masthead */}
      <div className="flex items-end justify-between gap-4 pb-3" style={{ borderBottom: `2px solid ${ACCENT}` }}>
        <div className="flex items-center gap-3">
          <img src={logoUrl} alt="" className="h-7 w-auto" />
          <div>
            <div className="font-mono text-[8px] uppercase tracking-[0.2em]" style={{ color: ACCENT }}>PageCoder · AI Visibility Scan</div>
            <div className="font-serif text-[20px] leading-none mt-1" style={{ color: INK }}>AI Visibility Report</div>
            <div className="text-[9px] mt-1" style={{ color: MUTED }}>{scope}</div>
          </div>
        </div>
        <div className="text-[8.5px] text-right leading-relaxed" style={{ color: MUTED }}>
          {new Date().toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}<br />
          {account?.email && <>{account.email}<br /></>}
          pagecoder.ai/ai-visibility-scan
        </div>
      </div>

      {/* 1 — where it stands */}
      <H n="01">Where you stand</H>
      {rows.length ? (
        <>
          <div className={`grid ${one ? 'grid-cols-4' : 'grid-cols-2'} gap-2 aivs-keep`}>
            <Tile label="AI readiness" value={standing.readiness ? `${standing.readiness.value}/100` : '-'}
              sub={`How well the page is built for AI to read it${signed(standing.readiness?.delta ?? null) ? ` · ${signed(standing.readiness!.delta)} since last scan of this page` : ''}`}
              tone={standing.readiness && standing.readiness.value >= 70 ? 'good' : standing.readiness && standing.readiness.value < 40 ? 'bad' : undefined} />
            {one && (
              <>
                <Tile label="AI named you" value={`${one.latest.visMentioned}/${one.latest.visTotal}`}
                  sub={`${setLabel(one.latest)} on ${engineLabel(one.engine)}${signed(one.delta) ? ` · ${signed(one.delta)} since its last run` : ''}`}
                  tone={one.latest.visMentioned ? 'good' : 'bad'} />
                <Tile label="Cited as a source" value={`${one.latest.visCited}/${one.latest.visTotal}`}
                  sub="Your page is a source, your name isn't said" />
              </>
            )}
            <Tile label="Knows you unprompted" value={knowsText(standing)}
              sub="From training memory, with no page in front of it"
              tone={Object.keys(standing.knows).length && Object.values(standing.knows).every((k) => k === 'no') ? 'bad' : Object.keys(standing.knows).length ? 'good' : undefined} />
          </div>
          {!one && standing.groups.length > 0 && (
            <div className="mt-2 aivs-keep">
              <p className="text-[9px] mb-1 leading-relaxed" style={{ color: MUTED }}>
                This range holds {standing.groups.length} question {standing.groups.length === 1 ? 'set' : 'sets'} on {standing.engines.length} {standing.engines.length === 1 ? 'engine' : 'engines'}.
                Each is a separate measurement, so there is no single score across them. The latest run of each set, per
                engine; a change is shown only against the previous run of the same set on the same engine.
              </p>
              <StandingTable standing={standing} ink />
            </div>
          )}
          <p className="text-[9px] mt-2 leading-relaxed" style={{ color: MUTED }}>
            Readiness is measured on the page and moves as soon as you change it. Being named is a live probe of what AI
            actually answers. It is sampled and non-deterministic, so read it as a trend. Off-site work typically takes
            weeks to show up.
          </p>
        </>
      ) : (
        <p style={{ color: MUTED }}>No scans in this range.</p>
      )}

      {/* What to change. Second, right under the numbers: it is the part of the
          report somebody acts on, and a PDF reader cannot go and find it in the tool. */}
      {plans.length > 0 && (
        <>
          <H n="01b">What to change, and why</H>
          <p className="text-[9px] mb-2 leading-relaxed" style={{ color: MUTED }}>
            From the latest run of every question set on every engine in this report, in the order it pays back.
            Each item says why it is on the list.
          </p>
          <ActionPlanView plans={plans} print palette={{ ink: INK, muted: MUTED, rule: RULE, accent: ACCENT, good: GOOD, bad: BAD, warn: '#E0B341' }} />
        </>
      )}

      {/* 2 — trend, one chart per question set */}
      <H n="02">Over time</H>
      {facets.length > 1 ? (
        <>
          <p className="text-[9px] mb-1.5 leading-relaxed" style={{ color: MUTED }}>
            One chart per question set. Different sets ask different questions, so they are never joined into one line.
            Solid is Gemini, dashed is GPT.
          </p>
          <div className="grid grid-cols-2 gap-3">
            {facets.map((f) => {
              const fs = seriesOf(f.rows);
              const fl = lines.filter((l) => fs.some((pt) => pt[l.key] != null));
              return (
                <div key={f.label} className="aivs-keep">
                  <div className="text-[9px] font-semibold mb-0.5" style={{ color: INK }}>{f.label}</div>
                  {fs.length > 1
                    ? <div className="h-[130px]"><TrendChart series={fs} lines={fl} breaks={breaksOf(f.rows)} ink /></div>
                    : <p className="text-[9px]" style={{ color: MUTED }}>One run ({engineLabel(f.rows[0]!.engine)}: {visLabel(f.rows[0]!)}). A trend needs two.</p>}
                </div>
              );
            })}
          </div>
        </>
      ) : series.length > 1 ? (
        <div className="aivs-keep">
          <div className="h-[250px]"><TrendChart series={series} lines={lines} breaks={breaks} ink /></div>
          {breaks.length > 0 && (
            <p className="text-[9px] mt-1" style={{ color: BAD }}>
              The questions in this set were edited {breaks.length}× in this range (dashed marks). Either side of a mark
              is a different list of questions.
            </p>
          )}
        </div>
      ) : (
        <p style={{ color: MUTED }}>Only one scan in this range. A trend needs at least two.</p>
      )}

      {/* 3 — engines disagree */}
      {comparison.length > 0 && (
        <div className="aivs-keep">
          <H n="03">Gemini vs GPT</H>
          <table className="w-full" style={{ borderCollapse: 'collapse' }}>
            <thead><tr style={{ borderBottom: `1px solid ${INK}` }}>
              <th className={th}>Engine</th><th className={th}>Question set</th><th className={th}>Readiness</th><th className={th}>Visibility</th><th className={th}>Footprint</th>
            </tr></thead>
            <tbody>
              {comparison.map((r) => (
                <tr key={r.engine} style={{ borderBottom: `1px solid ${RULE}` }}>
                  <td className={td}>{engineLabel(r.engine)}</td>
                  <td className={td}>{setLabel(r)}</td>
                  <td className={td}>{r.readinessScore ?? '-'}{r.readinessScore != null ? '/100' : ''}</td>
                  <td className={td}>{visLabel(r)}</td>
                  <td className={td}>{fpLabel(r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[9px] mt-1.5 leading-relaxed" style={{ color: MUTED }}>
            The two engines disagree more than people expect. That is the models, not your site, which is why one
            engine alone is a weak reading.
            {new Set(comparison.map(setLabel)).size > 1 && ' These two runs asked different question sets, so their visibility is not a comparison; section 01 compares the engines set by set.'}
          </p>
        </div>
      )}

      {/* 4 — competitors */}
      {competitive && competitive.rivals.length > 0 && (
        <div className="aivs-keep">
          <H n="04">Who AI names instead of you</H>
          <table className="w-full" style={{ borderCollapse: 'collapse' }}>
            <thead><tr style={{ borderBottom: `1px solid ${INK}` }}>
              <th className={th}>Named</th><th className={th}>Latest share</th><th className={th}>Direction</th><th className={th}>Seen since</th>
            </tr></thead>
            <tbody>
              {competitive.rivals.slice(0, 15).map((r) => {
                const first = r.points[0];
                const last = r.points[r.points.length - 1];
                const move = first && last && r.points.length > 1
                  ? Math.round((last.count / last.total) * 100) - Math.round((first.count / first.total) * 100) : null;
                return (
                  <tr key={r.name} style={{ borderBottom: `1px solid ${RULE}` }}>
                    <td className={td}>{r.name}</td>
                    <td className={td}>{r.latestShare}%</td>
                    <td className={td} style={{ color: move == null || move === 0 ? MUTED : move > 0 ? BAD : GOOD }}>
                      {move == null || move === 0 ? 'flat' : move > 0 ? `up ${move} pts` : `down ${Math.abs(move)} pts`}
                    </td>
                    <td className={td} style={{ color: MUTED }}>{new Date(r.firstSeen).toLocaleDateString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="text-[9px] mt-1.5 leading-relaxed" style={{ color: MUTED }}>
            Share = the portion of that run&apos;s questions where the name appeared in the answer. A rival trending up
            is taking answers you want; trending down is room opening up.
          </p>
        </div>
      )}

      {/* 5 — the target list */}
      {competitive && (competitive.lossSources.length > 0 || competitive.winSources.length > 0) && (
        <div className="aivs-keep">
          <H n="05">The pages behind those answers</H>
          <div className="grid grid-cols-2 gap-4">
            {competitive.lossSources.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold mb-1">Pages that beat you</p>
                <p className="text-[9px] mb-1.5 leading-relaxed" style={{ color: MUTED }}>
                  Cited in answers where you were absent, by how many of your questions they won. This is the off-site
                  target list, get named on these, or publish something better than what they have.
                </p>
                <ul className="text-[9.5px] leading-relaxed">
                  {competitive.lossSources.slice(0, 15).map((x) => (
                    <li key={x.host} className="flex justify-between gap-2" style={{ borderBottom: `1px solid ${RULE}` }}>
                      <span>{x.host}</span><span style={{ color: BAD }}>×{x.queries}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {competitive.winSources.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold mb-1">Pages carrying you</p>
                <p className="text-[9px] mb-1.5 leading-relaxed" style={{ color: MUTED }}>
                  Cited where AI did name or cite you. Whatever these do, the pages that lose do not, make more of them.
                </p>
                <ul className="text-[9.5px] leading-relaxed">
                  {competitive.winSources.slice(0, 15).map((x) => (
                    <li key={x.host} className="flex justify-between gap-2" style={{ borderBottom: `1px solid ${RULE}` }}>
                      <span>{x.host}</span><span style={{ color: GOOD }}>×{x.queries}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 6 — the log */}
      <H n="06">Every run in this range ({rows.length})</H>
      <table className="w-full" style={{ borderCollapse: 'collapse' }}>
        <thead><tr style={{ borderBottom: `1px solid ${INK}` }}>
          <th className={th}>Date</th><th className={th}>Domain</th><th className={th}>Engine</th><th className={th}>Questions</th>
          <th className={th}>Readiness</th><th className={th}>Named</th><th className={th}>Cited</th><th className={th}>Knows you</th>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} style={{ borderBottom: `1px solid ${RULE}` }}>
              <td className={td} style={{ whiteSpace: 'nowrap' }}>{new Date(r.createdAt).toLocaleDateString()}</td>
              <td className={td}>{r.domain}</td>
              <td className={td}>{engineLabel(r.engine)}</td>
              <td className={td} style={{ color: r.querySetName ? undefined : MUTED }}>{r.querySetName ?? '-'}</td>
              <td className={td}>{r.readinessScore ?? '-'}</td>
              <td className={td} style={{ color: r.visMentioned ? GOOD : undefined }}>
                {r.visTotal != null ? `${r.visMentioned}/${r.visTotal}` : '-'}
              </td>
              <td className={td}>{r.visTotal != null ? `${r.visCited}/${r.visTotal}` : '-'}</td>
              <td className={td}>{r.fpKnows ? (r.fpKnows === 'no' ? 'no' : r.fpKnows) : '-'}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td className={td} colSpan={7} style={{ color: MUTED }}>No scans in this range.</td></tr>}
        </tbody>
      </table>

      {/* 7 — the questions themselves. Counts without the questions behind them
          are a scoreboard nobody can act on. */}
      {(() => {
        const dv = ((detail?.detail.visibility ?? []) as DetailProbe[])
          .concat((detail?.detail.footprint as FootprintDetail | null)?.probes ?? []);
        if (dv.length === 0) return null;
        const group = (st: string): DetailProbe[] => dv.filter((p) => p.state === st);
        const named = group('mentioned'), citedOnly = group('cited_only'), absent = group('absent');
        const Block = ({ title, list, colour, blurb, rivals }: {
          title: string; list: DetailProbe[]; colour: string; blurb: string; rivals?: boolean;
        }): JSX.Element | null => list.length === 0 ? null : (
          <div className="aivs-keep mb-3">
            <p className="text-[9px] uppercase tracking-[0.14em] mb-0.5" style={{ color: colour }}>{title} · {list.length}</p>
            <p className="text-[8.5px] mb-1 leading-relaxed" style={{ color: MUTED }}>{blurb}</p>
            <ul className="text-[9.5px] leading-relaxed">
              {list.map((p, i) => (
                <li key={i} className="py-0.5" style={{ borderBottom: `1px solid ${RULE}` }}>
                  {p.query}
                  {rivals && p.competitors && p.competitors.length > 0 && (
                    <span style={{ color: MUTED }}>, named instead: <span style={{ color: BAD }}>{p.competitors.slice(0, 5).join(', ')}</span></span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        );
        return (
          <>
            <H n="07">Question by question · {detail?.domain}</H>
            <p className="text-[9px] mb-2 leading-relaxed" style={{ color: MUTED }}>
              The run of {detail ? new Date(detail.createdAt).toLocaleString() : ''}
              {detail?.querySetName ? ` · questions: ${detail.querySetName}` : ''}. The full answers and the sources
              behind each one are in the tool. They are too long to reprint here and they change as AI re-answers.
            </p>
            <Block title="Named" list={named} colour={GOOD} blurb="AI wrote your name in the answer. This is the win." />
            <Block title="Used your page, didn't name you" list={citedOnly} colour="#E0B341" rivals
              blurb="AI read these pages and answered without you. The cheapest fix in this report. It lives on a page you control." />
            <Block title="Not named" list={absent} colour={BAD} rivals
              blurb="AI never reached you here. Off-site presence, not page wording." />
          </>
        );
      })()}

      {/*
        07b — the complete list, which is the part a report is for.

        The block above is one run on one engine, grouped by outcome. It cannot
        say whether "not named" meant both models or only one, and it drops every
        question the newest run did not ask. A PDF is read by somebody who cannot
        click anything, so anything not printed does not exist for them.

        Every question in the filtered range, with the set that asks it and both
        models side by side, plus how many runs out of how many named you.
      */}
      {questions.length > 0 && (
        <div className="aivs-page-break">
          <H n="07b">Every question in this range ({questions.length})</H>
          <p className="text-[9px] mb-2 leading-relaxed" style={{ color: MUTED }}>
            The latest answer from each model, and how often it has named you across every run in this
            range. A blank model column means that model was never asked this question.
          </p>
          <table className="w-full" style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: PANEL, ...exact }}>
                <th className={th}>Question</th>
                <th className={th}>Question set</th>
                <th className={th}>Gemini</th>
                <th className={th}>GPT</th>
                <th className={th}>Named</th>
              </tr>
            </thead>
            <tbody>
              {questions.map((qq) => {
                const cell = (st: string | undefined): JSX.Element => {
                  if (!st) return <span style={{ color: MUTED }}>not asked</span>;
                  const label = st === 'mentioned' ? 'named' : st === 'cited_only' ? 'read, not named' : 'not named';
                  const colour = st === 'mentioned' ? GOOD : st === 'cited_only' ? '#A8760A' : BAD;
                  // The word as well as the colour: a report gets printed in
                  // black and white, photocopied, and read by people who cannot
                  // separate the two hues.
                  return <span style={{ color: colour }}>{label}</span>;
                };
                return (
                  <tr key={qq.query} style={{ borderTop: `1px solid ${RULE}` }}>
                    <td className={td} style={{ color: INK }}>{qq.query}</td>
                    <td className={td} style={{ color: MUTED }}>{qq.sets.join(', ') || '-'}</td>
                    <td className={td}>{cell(qq.latest.gemini)}</td>
                    <td className={td}>{cell(qq.latest.openai)}</td>
                    <td className={td} style={{ color: qq.wins > 0 ? GOOD : MUTED }}>
                      {qq.wins}/{qq.asked} runs
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* 8 — the plan lives in the tool, on purpose. */}
      <div className="aivs-keep mt-6 p-3 rounded-lg" style={{ border: `1px solid ${ACCENT}66`, background: PANEL, ...exact }}>
        <p className="text-[11px] font-semibold" style={{ color: INK }}>The current version is in the tool</p>
        <p className="text-[9px] mt-1 leading-relaxed" style={{ color: MUTED }}>
          Section 01b is the list as of today. It changes as your site and the answers do, so check the tool before
          you start on an item. It also has the full answers, every source cited, and the exact sentence to change on
          each page.
        </p>
        <p className="text-[9.5px] mt-1.5" style={{ color: ACCENT }}>
          {typeof window === 'undefined' ? '' : `${window.location.origin}${window.location.pathname}#account`}
        </p>
      </div>

      {/* How to read it — the report travels without whoever ran the scan. */}
      <div className="aivs-keep mt-6 pt-3" style={{ borderTop: `1px solid ${RULE}` }}>
        <p className="text-[10px] font-semibold mb-1">How to read this</p>
        <ul className="text-[9px] leading-relaxed" style={{ color: MUTED }}>
          <li><strong style={{ color: INK }}>Named</strong>, AI said your name in the answer. The outcome that counts.</li>
          <li><strong style={{ color: INK }}>Cited</strong>. Your page was one of the sources, but your name was never said. Half a win: the content works, the brand does not stick.</li>
          <li><strong style={{ color: INK }}>Knows you unprompted</strong>, whether the model recalls the brand from training alone, with no page in front of it. The slowest signal to move, and the strongest.</li>
          <li className="mt-1">These probes are sampled and non-deterministic: the same question can answer differently twice. Treat any single run as one reading, and the trend as the result.</li>
          <li>On-page fixes show up immediately. Listings, profiles and mentions have to be crawled and indexed first, which usually takes weeks.</li>
        </ul>
      </div>

      <p className="text-[8px] mt-4 pt-2" style={{ borderTop: `1px solid ${RULE}`, color: MUTED }}>
        AI Visibility Scan by PageCoder · Medialink s.r.o. · generated {new Date().toLocaleString()}
      </p>
    </div>
  );
}

export function RunDetailModal({ run, onClose, plan, onDelete }: {
  run: ScanRunDetail; onClose: () => void; plan?: 'free' | 'pro';
  /** Present only where the viewer may delete (not admin, not a share link). */
  onDelete?: () => void;
}): JSX.Element {
  // The print flag chooses WHICH region prints. It must not outlive the dialog,
  // or the next range export would silently print this one run instead.
  useEffect(() => {
    const clear = (): void => document.body.classList.remove('aivs-printing-run');
    window.addEventListener('afterprint', clear);
    return () => { clear(); window.removeEventListener('afterprint', clear); };
  }, []);
  const vis = (run.detail.visibility ?? []) as DetailProbe[];
  const fp = run.detail.footprint as FootprintDetail | null;
  const [copied, setCopied] = useState(false);
  const [faq, setFaq] = useState<FaqItem[]>([]);
  const questionList = [...new Set([...vis.map((p) => p.query), ...(fp?.probes ?? []).map((p) => p.query)].filter(Boolean))];
  const absentQuestions = [...new Set([...vis, ...(fp?.probes ?? [])].filter((p) => p.state === 'absent').map((p) => p.query).filter(Boolean))];
  const wins = [...vis, ...(fp?.probes ?? [])]
    .filter((p) => p.state === 'mentioned' || p.state === 'cited_only')
    .map((p) => ({ query: p.query, state: p.state as 'mentioned' | 'cited_only', citations: p.citations, detail: p.detail }));
  const faqText = questionList.join('\n');
  // Old runs already stored the citations and the competitors — the split into
  // "fixable on the page" and "nobody was named" needs nothing new from them.
  const brandName = (fp as { brand?: { name?: string } } | null)?.brand?.name ?? run.domain;
  // The stored full readiness scan (readinessFull) — powers a Pro Fix it plan (#7).
  const rd = run.detail.readiness;
  const readinessResult = rd && typeof rd === 'object' && 'readiness' in (rd as object) ? (rd as ScanResult) : null;
  function copyQuestions(): void {
    void navigator.clipboard.writeText(faqText).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  }
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
      <div id="aivs-print-run" className="w-full max-w-5xl bg-warm-dark border border-warm-dark-2 rounded-xl p-6 my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-1">
          <div>
            <h3 className="font-serif text-xl">{run.domain}</h3>
            <p className="font-mono text-sm text-text-secondary">
              {new Date(run.createdAt).toLocaleString()} · via {engineLabel(run.engine)}
              {run.querySetName && <> · questions: <span className="text-text-primary">{run.querySetName}</span></>}
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {faqText && (
              <button onClick={copyQuestions} title="Copy the generated questions, paste them into your site's FAQ and write your own answers" className="font-mono text-xs bg-cyan/10 text-cyan border border-cyan/40 rounded-lg px-3 py-1.5 hover:bg-cyan/20 transition">
                {copied ? 'copied ✓' : 'Copy questions'}
              </button>
            )}
            {/* One run, printable on its own. The history export covers a range;
                this is the single scan somebody wants to send to a colleague. */}
            <button
              onClick={() => { document.body.classList.add('aivs-printing-run'); window.print(); }}
              title="Save this one run as a PDF, every question, its verdict and the sources cited. Choose “Save as PDF” in the print dialog, and tick “Background graphics” if the preview looks blank."
              className="font-mono text-xs text-cyan border border-cyan/40 rounded-lg px-3 py-1.5 hover:bg-cyan/10 transition"
            >
              ↓ Export PDF
            </button>
            {onDelete && (
              <button onClick={onDelete} title="Delete this run permanently"
                className="font-mono text-xs text-text-secondary border border-warm-dark-2 rounded-lg px-3 py-1.5 hover:text-[#E0574A] hover:border-[#E0574A]/50 transition">
                Delete run
              </button>
            )}
            <button onClick={onClose} className="font-mono text-sm text-text-secondary hover:text-cyan">✕ close</button>
          </div>
        </div>
        {faqText && <p className="text-sm text-text-secondary mb-4">Tip: use these as FAQ questions on your own site and write your own answers. That's how you get cited.</p>}

        <div className="grid grid-cols-3 gap-3 mb-5">
          <ModalStat label="Readiness" value={run.readinessScore != null ? `${run.readinessScore}/100` : '-'} />
          {/* Just the result. "0 cited" next to it is a loss reported as a
              statistic, in the first place anybody looks, and it competes with
              the number that is actually the headline. The cited/not-named split
              is below, where it belongs, with its own explanation. */}
          <ModalStat label="Named by AI" value={run.visTotal != null ? `${run.visMentioned}/${run.visTotal}` : '-'} />
          <ModalStat label="Footprint" value={run.fpTotal ? `${run.fpKnows === 'no' ? 'unknown' : 'knows'} · ${run.fpMentioned}/${run.fpTotal}` : 'not asked'} />
        </div>

        {/* What the three verdicts mean — the badges below are the whole point of
            re-opening an old run, so spell them out once (B1). */}
        {(vis.length > 0 || (fp?.probes?.length ?? 0) > 0) && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mb-4 pb-3 border-b border-warm-dark-2">
            {(['mentioned', 'cited_only', 'absent'] as const).map((k) => (
              <span key={k} className="flex items-center gap-1.5">
                <span className={`font-mono text-[11px] uppercase tracking-wide font-semibold rounded px-2 py-0.5 ${STATE_UI[k]!.cls}`}>{STATE_UI[k]!.label}</span>
                <span className="text-sm text-text-secondary">{STATE_UI[k]!.hint}</span>
              </span>
            ))}
          </div>
        )}

        {vis.length > 0 && (
          <div className="mb-4">
            <h4 className="font-mono text-sm text-text-secondary mb-2">Visibility questions ({engineLabel(run.engine)})</h4>
            <div className="flex flex-col gap-2">
              {vis.map((p, i) => <ProbeRow key={i} p={p} />)}
            </div>
          </div>
        )}
        {fp?.probes && fp.probes.length > 0 && (
          <div>
            <h4 className="font-mono text-sm text-text-secondary mb-2">
              Footprint questions
              {fp.knowledge?.knows && <span className="ml-2 text-text-secondary/70">· from training alone, AI {fp.knowledge.knows === 'no' ? 'does not know this brand' : `knows this brand ${fp.knowledge.knows}`}</span>}
            </h4>
            <div className="flex flex-col gap-2">
              {fp.probes.map((p, i) => <ProbeRow key={i} p={p} />)}
            </div>
          </div>
        )}

        <RunRivals probes={[...vis, ...(fp?.probes ?? [])]} domain={run.domain} />

        {/* The pages AI read and answered without you, for this past run. */}
        <ClaimYourPages
          probes={[...vis, ...(fp?.probes ?? [])]}
          brand={{ name: brandName, domain: run.domain }}
          lang={null}
          isPro={plan === 'pro'}
        />

        {/* Pro: the full Fix it action plan for this exact past scan (#7). */}
        {plan === 'pro' && readinessResult && (
          <details className="mt-5 border-t border-warm-dark-2 pt-4">
            <summary className="font-mono text-sm text-cyan cursor-pointer">Fix it. Your action plan for this scan ↓</summary>
            <div className="mt-3">
              <FixIt result={readinessResult} questions={questionList} faq={faq} setFaq={setFaq} absentQuestions={absentQuestions} wins={wins} />
            </div>
          </details>
        )}
        {plan === 'free' && (
          <p className="mt-5 border-t border-warm-dark-2 pt-4 text-sm text-text-secondary/80 italic">
            The Fix it action plan for this scan is a Pro feature.
          </p>
        )}
      </div>
    </div>
  );
}

function ModalStat({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="border border-warm-dark-2 rounded-lg px-3 py-2">
      <div className="font-mono text-sm text-text-secondary">{label}</div>
      <div className="font-mono text-text-primary">{value}</div>
    </div>
  );
}

const STATE_UI: Record<string, { label: string; hint: string; cls: string }> = {
  mentioned: { label: 'MENTIONED', hint: 'named in the answer itself. The outcome that counts', cls: 'bg-success text-warm-dark' },
  cited_only: { label: 'NOT NAMED, BUT USED', hint: 'the model read your page and used it, but wrote the answer without your name. A brand-clarity problem, not a reach problem', cls: 'bg-[#E0B341] text-warm-dark' },
  absent: { label: 'ABSENT', hint: 'not named, not cited, competitors got the answer', cls: 'bg-[#E0574A] text-warm-dark' },
};

/**
 * One probed question in a past run (B1): the full question, the verdict spelled
 * out, and the WHOLE answer the engine gave — that answer is the evidence for
 * the verdict, so it is shown in full (scrollable, expandable) rather than clipped.
 */
function ProbeRow({ p }: { p: DetailProbe }): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const ui = STATE_UI[p.state] ?? { label: p.state.toUpperCase(), hint: '', cls: 'bg-warm-dark-2 text-text-primary' };
  const answer = p.answer ?? '';
  // Runs stored before full answers were kept were cut at 2 000 chars (B1).
  const legacyCut = p.answerTruncated === undefined && answer.length >= 1990;

  return (
    <div className="border border-warm-dark-2 rounded-lg p-3">
      <div className="flex items-start justify-between gap-3">
        <p className="text-text-primary">{p.query}</p>
        <span className="flex items-center gap-1.5 shrink-0">
          <span title={ui.hint} className={`font-mono text-[11px] uppercase tracking-wide font-semibold rounded px-2 py-0.5 ${ui.cls}`}>
            {ui.label}
          </span>
          <Info term={p.state} />
          {/* No strict majority across the samples — a coin flip, not a verdict. */}
          {p.stable === false && (
            <>
              <span title="This state did not hold a majority of the samples. The models disagreed with themselves." className="font-mono text-[11px] uppercase tracking-wide border rounded px-1.5 py-0.5 text-estimated border-estimated/40">
                unstable
              </span>
              <Info term="stable" />
            </>
          )}
        </span>
      </div>
      {p.detail && <p className="font-mono text-xs text-text-secondary mt-1">{p.detail}</p>}

      {answer && (
        <div className="mt-2">
          {/* Hidden until asked for. With 25 questions the answers turned the
              detail into a wall of text you had to scroll past to reach the
              verdicts, which are the reason for opening an old run at all. */}
          <button
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            className={`font-mono text-xs rounded-lg px-2.5 py-1.5 border transition ${
              expanded
                ? 'text-cyan border-cyan/60 bg-cyan/10'
                : 'text-text-secondary border-[#70707D] hover:text-cyan hover:border-cyan/60'
            }`}
          >
            {expanded ? 'hide the answer ↑' : `show the answer AI gave ↓${answer ? ` (${answer.length.toLocaleString()} chars)` : ''}`}
          </button>
          <div className={expanded ? 'block mt-2' : 'hidden'}>
          <div className="text-sm text-text-secondary whitespace-pre-wrap break-words border border-warm-dark-2 rounded bg-warm-dark-2/30 p-3">
            {answer}
          </div>
          {(p.answerTruncated || legacyCut) && (
            <p className="font-mono text-xs text-estimated mt-1">
              {p.answerTruncated
                ? 'Answer hit the storage cap and is cut short here.'
                : 'Stored before full answers were kept, cut at 2 000 characters. Re-scan to capture it whole.'}
            </p>
          )}
          </div>
        </div>
      )}

      {p.citations && p.citations.length > 0 && (
        <div className="mt-2">
          <p className="font-mono text-xs text-text-secondary mb-1">Sources it cited ({p.citations.length})</p>
          <ul className="text-sm space-y-0.5">
            {p.citations.map((c, i) => (
              <li key={i} className="truncate">
                {/^https?:\/\//.test(c)
                  ? <a href={externalHref(c)} target="_blank" rel="noreferrer" className="text-cyan/80 hover:text-cyan hover:underline">{c}</a>
                  : <span className="text-text-secondary">{c}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {p.competitors && p.competitors.length > 0 && (
        <div className="mt-2">
          <p className="font-mono text-xs text-text-secondary mb-1">Named instead of / alongside you</p>
          <div className="flex flex-wrap gap-1.5">
            {p.competitors.map((c) => (
              <span key={c} className="font-mono text-xs border border-warm-dark-2 rounded px-2 py-0.5 text-text-secondary">{c}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
